import React, { useState } from "react";
import api, { pollJob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Empty } from "@/components/common";
import Mermaid from "@/components/Mermaid";
import { toast } from "sonner";
import { Loader2, Boxes, Network, Workflow, Sparkles, ListTree } from "lucide-react";

const DIR_COLOR = { inbound: "bg-[#29abe2]/15 text-[#1a7bb0]", outbound: "bg-[#c9a227]/15 text-[#a6821a]", bidirectional: "bg-emerald-500/15 text-emerald-600" };

export default function SolutionDesign({ oid, opp, refresh }) {
  const [busy, setBusy] = useState(false);
  const d = opp?.solution_design;
  const hasRfq = !!opp?.rfq_profile;

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/opportunities/${oid}/solution-design/generate`);
      await pollJob(r.data.job_id);
      toast.success("Architecture generated");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Generation failed"); }
    finally { setBusy(false); }
  };

  if (!hasRfq) return <Empty title="Analyze the RFQ first" hint="The architecture is derived from the RFQ profile and your content library." />;

  return (
    <div className="space-y-5">
      <div className="rounded-xl border bg-card p-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 font-semibold text-[#0f2557] dark:text-[#29abe2]"><Workflow size={18} /> Solution Design — Architecture Builder</div>
        <Button onClick={generate} disabled={busy} className="gap-2" data-testid="gen-architecture">
          {busy ? <Loader2 className="animate-spin" size={15} /> : <Sparkles size={15} />}
          {d ? "Regenerate architecture" : "Auto-draw architecture"}
        </Button>
      </div>

      {!d ? <Empty title="No architecture yet" hint="Auto-draw logical & deployment diagrams from the RFQ and Yanthra content library." /> : (
        <div className="space-y-5 animate-fade-up">
          <div className="rounded-xl border bg-card p-5">
            <div className="text-[11px] font-semibold tracking-[0.18em] text-[#29abe2] uppercase mb-1">Overview</div>
            <h3 className="text-lg font-semibold text-foreground">{d.title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{d.overview}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="rounded-xl border bg-card p-5" data-testid="logical-diagram">
              <div className="flex items-center gap-2 font-semibold text-[#0f2557] dark:text-[#29abe2] mb-3"><Boxes size={16} /> Logical architecture</div>
              <Mermaid chart={d.logical_mermaid} id="logical" />
            </div>
            <div className="rounded-xl border bg-card p-5" data-testid="deployment-diagram">
              <div className="flex items-center gap-2 font-semibold text-[#0f2557] dark:text-[#29abe2] mb-3"><Network size={16} /> Deployment architecture</div>
              <Mermaid chart={d.deployment_mermaid} id="deployment" />
            </div>
          </div>

          {(d.components || []).length > 0 && (
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-2 font-semibold text-[#0f2557] dark:text-[#29abe2] mb-3"><ListTree size={16} /> Components</div>
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="text-left text-muted-foreground border-b"><th className="py-1.5 pr-3">Component</th><th className="pr-3">Layer</th><th className="pr-3">Product</th><th>Responsibility</th></tr></thead>
                <tbody>{d.components.map((c, i) => (
                  <tr key={i} className="border-b border-border/40"><td className="py-1.5 pr-3 font-medium text-foreground">{c.name}</td><td className="pr-3">{c.layer}</td><td className="pr-3">{c.product || "—"}</td><td className="text-muted-foreground">{c.responsibility}</td></tr>
                ))}</tbody>
              </table></div>
            </div>
          )}

          {(d.integrations || []).length > 0 && (
            <div className="rounded-xl border bg-card p-5">
              <div className="font-semibold text-[#0f2557] dark:text-[#29abe2] mb-3">Integrations</div>
              <div className="space-y-2">{d.integrations.map((it, i) => (
                <div key={i} className="flex items-center gap-2 text-sm flex-wrap">
                  <span className="font-mono text-[10px] text-muted-foreground w-14">{it.id}</span>
                  <span className="text-foreground flex-1 min-w-[180px]">{it.name}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-secondary">{it.pattern}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded ${DIR_COLOR[it.direction] || "bg-secondary"}`}>{it.direction}</span>
                  <span className="text-[10px] text-muted-foreground">{it.protocol}</span>
                </div>
              ))}</div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {(d.nfr || []).length > 0 && (
              <div className="rounded-xl border bg-card p-5"><div className="font-semibold text-[#0f2557] dark:text-[#29abe2] mb-2">Non-functional targets</div>
                {d.nfr.map((n, i) => <div key={i} className="flex justify-between text-sm py-0.5"><span className="text-muted-foreground">{n.area}</span><span className="text-foreground font-medium">{n.target}</span></div>)}
              </div>
            )}
            {(d.assumptions || []).length > 0 && (
              <div className="rounded-xl border bg-card p-5"><div className="font-semibold text-[#0f2557] dark:text-[#29abe2] mb-2">Assumptions</div>
                <ul className="text-sm text-muted-foreground list-disc pl-4 space-y-0.5">{d.assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
